/**
 * <why-us-collage> — sections/why-us.liquid.
 * Ties the photo tilt to scroll: `--tilt-progress` goes from 0 (collage top at 75% of the
 * viewport height) to 1 (collage top at 30%) and back again when scrolling up.
 * CSS multiplies each photo's `--tilt` by it. With reduced motion the tilt is shown at 100%.
 */
class WhyUsCollage extends HTMLElement {
  #ticking = false;

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.style.setProperty('--tilt-progress', '1');
      return;
    }

    // Capture phase so a scrolling ancestor (not only the window) also drives it
    document.addEventListener('scroll', this.#onScroll, { capture: true, passive: true });
    window.addEventListener('resize', this.#onScroll);
    this.#update();
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.#onScroll, { capture: true });
    window.removeEventListener('resize', this.#onScroll);
  }

  #onScroll = () => {
    if (this.#ticking) return;
    this.#ticking = true;
    requestAnimationFrame(() => {
      this.#ticking = false;
      this.#update();
    });
  };

  #update() {
    if (!window.innerHeight) return; // hidden/background tab: nothing to measure against
    const { top } = this.getBoundingClientRect();
    // The tilt runs while the collage top travels from 75% to 30% of the viewport height, so
    // the photos are already well inside the screen when they start to turn.
    const start = window.innerHeight * 0.75; // → 0
    const end = window.innerHeight * 0.3; // → 1
    const progress = Math.min(Math.max((start - top) / (start - end), 0), 1);
    this.style.setProperty('--tilt-progress', progress.toFixed(3));
  }
}

if (!customElements.get('why-us-collage')) {
  customElements.define('why-us-collage', WhyUsCollage);
}
